/**
 * Tiny helper for workspace stores.
 *
 * Each workspace tool (Cost Tracker, Contacts, Milestones, Checklists) needs
 * the exact same lifecycle:
 *   - on mount, if Supabase is configured AND the user is signed in,
 *     try a `select` on the table; on success, switch to "remote" mode
 *     and use it for every read/write
 *   - if the select errors (most commonly: the table hasn't been created
 *     yet in the user's Supabase project), fall back to "local" mode
 *     (localStorage) so the app still works while the user runs the SQL
 *
 * Returning the detected `mode` lets each hook decide whether to await
 * remote writes or just mutate local state.
 */
import { useEffect, useState } from 'react';
import { getSupabase, isSupabaseConfigured } from './supabaseClient';

export const useBackendMode = (tableName) => {
  const [mode, setMode] = useState('booting'); // 'booting' | 'remote' | 'local'

  useEffect(() => {
    let cancelled = false;
    const detect = async () => {
      if (!isSupabaseConfigured()) {
        if (!cancelled) setMode('local');
        return;
      }
      try {
        const supabase = getSupabase();
        const { data: sessionData } = await supabase.auth.getSession();
        if (!sessionData?.session?.user?.id) {
          if (!cancelled) setMode('local');
          return;
        }
        // Probe the table, limit 1 keeps it cheap. If the table is missing
        // Postgres returns code 42P01 → fall back to local.
        const { error } = await supabase.from(tableName).select('*', { count: 'exact', head: true });
        if (cancelled) return;
        if (error) {
          // Common: table doesn't exist yet, or RLS forbids the probe.
          // Either way we cannot use remote mode safely.
          // eslint-disable-next-line no-console
          console.warn(`[workspace] ${tableName}: falling back to local mode -`, error.message);
          setMode('local');
        } else {
          setMode('remote');
        }
      } catch (e) {
        if (cancelled) return;
        // eslint-disable-next-line no-console
        console.warn(`[workspace] ${tableName}: backend probe failed -`, e?.message);
        setMode('local');
      }
    };
    detect();
    return () => { cancelled = true; };
  }, [tableName]);

  return mode;
};
